'use client';

import { useCerFeatures } from './use-cer-features';

import { useMemo } from 'react';
import { buildSystemAlerts, type SystemAlert, type SystemAlertAudience } from '@/lib/system-alerts';
import { useDashboardSummary } from '@/hooks/use-dashboard-summary';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useForecastData } from '@/hooks/use-forecast-data';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { useWeatherData } from '@/hooks/use-weather-data';

/**
 * Raccoglie i dati da cui nascono gli avvisi di sistema del centro notifiche.
 *
 * Le fetch che servono solo all'amministratore (KPI dashboard, previsione,
 * meteo) ricevono `orgId` nullo quando il chiamante è un socio: gli hook
 * scoperti da `orgId` non partono, e la pagina del socio non chiede endpoint
 * che non le competono.
 */
export function useSystemAlerts(
	orgId: string | null | undefined,
	audience: SystemAlertAudience,
): SystemAlert[] {
	const { features } = useCerFeatures(orgId);
	const adminOrgId = audience === 'admin' ? (orgId ?? null) : null;

	const { config } = useScopedConfig(orgId);
	const { totals } = useEnergyData(undefined, orgId);
	const { summary } = useDashboardSummary(adminOrgId, 'mese');
	const { forecast, production, source } = useForecastData({
		orgId: adminOrgId,
		realProd: totals.totalImmessa,
		realCons: totals.totalPrelevata,
		realShared: totals.totalCondivisa,
		kwp: config.kwp,
		cons: config.cons ?? undefined,
	});
	const { weather } = useWeatherData(adminOrgId);

	return useMemo(
		() =>
			buildSystemAlerts(
				{
					month: summary?.month ?? null,
					totals,
					forecast,
					forecastPeakDate: production?.peakDate ?? null,
					forecastSource: source,
					cloudCoverPct: weather?.cloudCoverPct ?? null,
				},
				audience,
			).filter(
				(alert) =>
					(features.reports || alert.id !== 'sys-report-builder') &&
					(features.aiSuggestions !== 'disabled' || !alert.id.startsWith('sys-ai-')),
			),
		[features, summary, totals, forecast, production, source, weather, audience],
	);
}
